(() => {
  // Work with option names, never variant IDs: a style spans all shoe sizes.
  function buildGroups(styles, config = {}) {
    const available = new Set(styles);
    const assigned = new Set();
    const warnings = [];
    const fallbackHeading = config.fallbackHeading || "Classics";
    const groups = [];
    const entries = Array.isArray(config.groups) ? config.groups : [];
    if (entries.length > 3) warnings.push("Only the first three curated groups are used.");

    entries.slice(0, 3).forEach((entry) => {
      const heading = typeof entry?.heading === "string" ? entry.heading.trim() : "";
      if (!heading) {
        warnings.push("A group has no heading; its styles fall back to Classics.");
        return;
      }
      const group = { heading, styles: [] };
      (Array.isArray(entry.styles) ? entry.styles : []).forEach((style) => {
        if (!available.has(style)) warnings.push(`Unknown style: ${style}`);
        else if (assigned.has(style)) warnings.push(`Duplicate style: ${style}`);
        else {
          assigned.add(style);
          group.styles.push(style);
        }
      });
      groups.push(group);
    });

    const unassigned = styles.filter((style) => !assigned.has(style));
    if (unassigned.length) {
      let fallback = groups.find((group) => group.heading === fallbackHeading);
      if (!fallback) {
        fallback = { heading: fallbackHeading, styles: [] };
        groups.push(fallback);
      }
      fallback.styles.push(...unassigned);
    }
    return { groups: groups.filter((group) => group.styles.length), warnings };
  }

  // One badge per style; the first badge entry listing a style wins, as with groups.
  function buildBadges(styles, config = {}) {
    const available = new Set(styles);
    const badges = new Map();
    const warnings = [];
    const text = (value) => (typeof value === "string" ? value.trim() : "");
    (Array.isArray(config.badges) ? config.badges : []).forEach((entry) => {
      const label = text(entry?.label);
      if (!label) {
        warnings.push("A badge has no label; it is skipped.");
        return;
      }
      const badge = { label, color: text(entry.color), textColor: text(entry.textColor) };
      (Array.isArray(entry.styles) ? entry.styles : []).forEach((style) => {
        if (!available.has(style)) warnings.push(`Unknown badge style: ${style}`);
        else if (badges.has(style)) warnings.push(`Style has more than one badge: ${style}`);
        else badges.set(style, badge);
      });
    });
    return { badges, warnings };
  }

  if (typeof module !== "undefined" && module.exports) module.exports = { buildGroups, buildBadges };
  if (typeof customElements === "undefined" || customElements.get("swatch-groups")) return;

  customElements.define("swatch-groups", class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      const source = this.querySelector("[data-swatch-options]");
      const configNode = this.querySelector("[data-swatch-groups-config]");
      if (!source || !configNode) return;
      let config;
      try {
        config = JSON.parse(configNode.textContent);
      } catch (error) {
        console.warn("[Swatch groups] Invalid configuration; using the standard picker.", error);
        return;
      }

      const controls = new Map();
      source.querySelectorAll("input[type='radio']").forEach((input) => {
        controls.set(input.value, input.closest(".variant-picker__option"));
      });
      const styles = [...controls.keys()];
      const { badges, warnings: badgeWarnings } = buildBadges(styles, config);
      badges.forEach((badge, style) => this.addBadge(controls.get(style), badge));

      // Badges alone keep Zest's flat picker; only curated groups add headings.
      if (!Array.isArray(config.groups) || !config.groups.length) {
        badgeWarnings.forEach((warning) => console.warn(`[Swatch groups] ${warning}`));
        source.classList.add("swatch-group__values");
        this.dataset.initialized = "true";
        return;
      }

      const { groups, warnings } = buildGroups(styles, config);
      [...warnings, ...badgeWarnings].forEach((warning) => console.warn(`[Swatch groups] ${warning}`));
      const fragment = document.createDocumentFragment();

      groups.forEach((group, index) => {
        const container = document.createElement("div");
        container.className = "swatch-group";
        container.setAttribute("role", "group");
        // Same classes as Zest's "Size:" label so the typography matches.
        const title = document.createElement("div");
        title.className = "form__label swatch-group__label";
        title.id = `SwatchGroup-${this.dataset.sectionId}-${index}`;
        const heading = document.createElement("span");
        heading.className = "font-semibold swatch-group__heading";
        heading.dataset.heading = group.heading;
        heading.textContent = group.heading;
        const selected = document.createElement("span");
        selected.className = "swatch-group__selected";
        title.append(heading, " ", selected);
        container.setAttribute("aria-labelledby", title.id);
        const values = document.createElement("div");
        values.className = "variant-picker__option-values swatch-group__values";
        group.styles.forEach((style) => {
          values.append(controls.get(style));
        });
        container.append(title, values);
        fragment.append(container);
      });
      source.replaceWith(fragment);
      this.updateSelection();
      this.addEventListener("change", () => this.updateSelection());
      this.dataset.initialized = "true";
    }

    // The checked style's group reads "Animal: Hummingbird Blue-Pink"; other groups show only their heading.
    updateSelection() {
      this.querySelectorAll(".swatch-group").forEach((group) => {
        const heading = group.querySelector(".swatch-group__heading");
        const checked = [...group.querySelectorAll("input[type='radio']")].find((input) => input.checked);
        heading.textContent = checked ? `${heading.dataset.heading}:` : heading.dataset.heading;
        group.querySelector(".swatch-group__selected").textContent = checked ? checked.value : "";
      });
    }

    addBadge(control, badge) {
      const input = control.querySelector("input");
      const element = document.createElement("span");
      element.className = "swatch-badge";
      element.id = `${input.id}-badge`;
      element.dataset.badge = badge.label.toLowerCase();
      element.textContent = badge.label;
      if (badge.color) element.style.setProperty("--swatch-badge-bg", badge.color);
      if (badge.textColor) element.style.setProperty("--swatch-badge-fg", badge.textColor);
      control.classList.add("has-swatch-badge");
      control.append(element);
      input.setAttribute("aria-describedby", [input.getAttribute("aria-describedby"), element.id].filter(Boolean).join(" "));
    }
  });
})();
